import { useState, type FormEvent } from 'react';
import { useAppStore } from '../../stores/appStore';
import type { AlertLevel } from '../../lib/api';
import { LEVEL_META } from './AlertOverlay';

const LEVELS: AlertLevel[] = ['L1', 'L2', 'L3', 'L911'];

/**
 * Send a full-screen family alert to every device (phones, PC, Pi TV).
 */
export function ComposeAlert() {
  const open = useAppStore((s) => s.composeAlertOpen);
  const setOpen = useAppStore((s) => s.setComposeAlertOpen);
  const sendAlert = useAppStore((s) => s.sendAlert);
  const [level, setLevel] = useState<AlertLevel>('L1');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) {
      setError('Write a message');
      return;
    }
    setSending(true);
    setError('');
    try {
      await sendAlert(level, body.trim());
      setBody('');
      setLevel('L1');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  }

  const meta = LEVEL_META[level];

  return (
    <div
      className="modal-backdrop fixed inset-0 z-[150] flex items-end justify-center bg-black/60 sm:items-center sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-lg overflow-hidden rounded-t-3xl border border-slate-700 bg-slate-900 shadow-2xl sm:rounded-3xl"
      >
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <h2 className="text-lg font-bold">Send family alert</h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-lg px-2 py-1 text-slate-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
              Priority
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {LEVELS.map((lv) => {
                const m = LEVEL_META[lv];
                const selected = level === lv;
                return (
                  <button
                    key={lv}
                    type="button"
                    onClick={() => setLevel(lv)}
                    className={`rounded-xl border-2 px-2 py-3 text-center transition ${
                      selected
                        ? `${m.bg} ${m.text} border-white shadow-lg scale-[1.02]`
                        : 'border-slate-700 bg-slate-800 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    <div className="emoji-font text-2xl">{m.emoji}</div>
                    <div className="mt-1 text-xs font-black">{lv}</div>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              L1 yellow · L2 orange · L3 urgent flash · L911 emergency red
            </p>
          </div>

          <label className="block">
            <span className="mb-1 block text-sm text-slate-400">Message</span>
            <textarea
              autoFocus
              rows={4}
              className="w-full rounded-xl border border-slate-600 bg-slate-800 px-3 py-3 text-base outline-none focus:border-violet-500"
              placeholder="What should everyone see?"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          {/* Live preview strip */}
          <div
            className={`rounded-xl border-2 px-3 py-3 ${meta.bg} ${meta.border} ${meta.text}`}
          >
            <div className="flex items-center gap-2">
              <span className={`emoji-font text-2xl ${meta.pulseClass}`}>
                {meta.emoji}
              </span>
              <span className="text-sm font-bold">{meta.label}</span>
            </div>
            <p className="mt-1 text-sm font-semibold">
              {body.trim() || 'Your message preview…'}
            </p>
          </div>

          {error && (
            <p className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex gap-2 border-t border-slate-800 p-4">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-xl bg-slate-800 px-4 py-3 font-semibold"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={sending}
            className="flex-1 rounded-xl bg-violet-600 py-3 text-base font-bold text-white hover:bg-violet-500 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send to all devices'}
          </button>
        </div>
      </form>
    </div>
  );
}
